import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, X, Plus, Info, Loader2 } from 'lucide-react';
import { connect } from 'react-redux';
import { getSkills } from '../../../store/actions';
import { getAllUsersBySkills, createNewSkill, openMainModal, closeMainModal } from '../../../store/actions/profileActions';
import { RubroOption } from '../../../types/newProject';
import styles from './RubroSelector.module.scss';

interface RubroSelectorProps {
  onRubroSelect: (rubroId: number, rubroName: string) => void;
  existingRubros: Array<{ name: string; id?: string }>;
  selectedRubro?: string;
  onClear?: () => void;
  disabled?: boolean;
  skillsList: any[];
  skillsCatalog: Array<{ id: number; name: string }>;
  newSkill: any;
  getAllUsersBySkills: () => void;
  getSkills: () => void;
  createNewSkill: (skillName: string, isNew: boolean) => void;
  openMainModal: (modalData: any) => void;
  closeMainModal: () => void;
}

const RubroSelector: React.FC<RubroSelectorProps> = ({
  onRubroSelect,
  existingRubros,
  selectedRubro = "",
  onClear,
  disabled = false,
  skillsList,
  skillsCatalog,
  newSkill,
  getAllUsersBySkills,
  getSkills,
  createNewSkill,
  openMainModal,
  closeMainModal
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [showCompleteList, setShowCompleteList] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [isLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getAllUsersBySkills();
    getSkills();
  }, [getAllUsersBySkills, getSkills]);

  const validateInput = useCallback((value: string) => {
    const regex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/;
    
    if (value.length > 0 && value.length < 3) {
      setIsValid(false);
      setValidationMessage('Mínimo 3 caracteres');
    } else if (value.length >= 3 && !regex.test(value)) {
      setIsValid(false);
      setValidationMessage('Solo letras permitidas');
    } else {
      setIsValid(true);
      setValidationMessage('');
    }
  }, []);

  useEffect(() => {
    if (newSkill && newSkill.id && newSkill.name) {
      onRubroSelect(newSkill.id, newSkill.name);
      setShowSuggestions(false);
      setInputValue("");
      setShowCompleteList(false);
    }
  }, [newSkill, onRubroSelect]);


  const normalizeString = (str: string): string => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  };

  const handleInputChange = useCallback((value: string) => {
    validateInput(value);
    
    if (value.length < 3) {
      setShowSuggestions(false);
    } else if (value.length >= 3) {
      setShowSuggestions(true);
    }
    
    setInputValue(value);
  }, [validateInput]);

  const userCountBySkillId = useMemo(() => {
    const m = new Map<number, number>();
    for (const rubro of skillsList) {
      const id = Number(rubro.id);
      if (!Number.isFinite(id)) continue;
      m.set(id, Number(rubro.users) || 0);
    }
    return m;
  }, [skillsList]);

  const suggestionSource = skillsCatalog.length > 0 ? skillsCatalog : skillsList;

  const filteredSuggestions = useMemo(() => {
    const q = inputValue.toLowerCase();
    const rows: RubroOption[] = suggestionSource
      .map((rubro: { id: number | string; name: string }) => {
        const id = Number(rubro.id);
        return {
          id,
          name: rubro.name,
          users: userCountBySkillId.get(id) ?? 0,
        };
      })
      .filter((r) => Number.isFinite(r.id) && r.name.toLowerCase().includes(q));
    rows.sort((a, b) => {
      if (b.users !== a.users) return b.users - a.users;
      return a.name.localeCompare(b.name, 'es');
    });
    return rows;
  }, [suggestionSource, inputValue, userCountBySkillId]);

  const fullCatalogList = useMemo(() => {
    const rows: RubroOption[] = suggestionSource
      .map((rubro: { id: number | string; name: string }) => {
        const id = Number(rubro.id);
        return {
          id,
          name: rubro.name,
          users: userCountBySkillId.get(id) ?? 0,
        };
      })
      .filter((r) => Number.isFinite(r.id));
    rows.sort((a, b) => a.name.localeCompare(b.name, 'es'));
    return rows;
  }, [suggestionSource, userCountBySkillId]);

  const handleRubroSelect = useCallback((rubro: RubroOption) => {
    if (existingRubros.some((existingRubro) => existingRubro.name === rubro.name)) {
      openMainModal({
        title: `Ups, ya tienes un rubro con el nombre ${rubro.name}`,
        iconType: "error",
        body: `Si deseas editarlo o eliminarlo, puedes hacerlo desde la lista de "Agregados".`,
        isSuccess: true,
        confirmHandler: null,
      });
      return;
    }

    onRubroSelect(rubro.id, rubro.name);
    setShowSuggestions(false);
    setInputValue("");
    setShowCompleteList(false);
  }, [existingRubros, onRubroSelect, openMainModal]);

  const handleCreateRubro = useCallback(async () => {
    if (inputValue.length >= 3 && isValid) {
      try {
        setIsCreating(true);
        
        const normalizedInputValue = normalizeString(inputValue);
        const matchedRaw = suggestionSource.find(
          (rubro: { name: string }) => normalizeString(rubro.name) === normalizedInputValue
        );

        if (!matchedRaw) {
          createNewSkill(inputValue, true);
          setTimeout(() => {
            getAllUsersBySkills();
            getSkills();
          }, 2000);
        } else {
          const id = Number(matchedRaw.id);
          handleRubroSelect({
            id,
            name: matchedRaw.name,
            users: userCountBySkillId.get(id) ?? 0,
          });
        }
      } catch (error) {
        console.error('Error creando nuevo rubro:', error);
      } finally {
        setIsCreating(false);
      }
    }
  }, [
    inputValue,
    isValid,
    suggestionSource,
    handleRubroSelect,
    createNewSkill,
    getAllUsersBySkills,
    getSkills,
    userCountBySkillId,
  ]);

  const handleCancel = useCallback(() => {
    setInputValue("");
    setShowSuggestions(false);
    setShowCompleteList(false);
    onClear?.();
  }, [onClear]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      if (containerRef.current && !containerRef.current.contains(target)) {
        setShowSuggestions(false);
        setShowCompleteList(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={containerRef} className={styles.rubroSelector}>
      {selectedRubro !== "" ? (
        <div className={styles.selectedRubro}>
          <div className={styles.selectedRubroLabel}>
            {selectedRubro}
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleCancel}
              disabled={disabled}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.inputContainer}>
            <div className={styles.inputWrapper}>
              <Search size={18} className={styles.searchIcon} />
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                onFocus={() => {
                  if (inputValue.length >= 3) {
                    setShowSuggestions(true);
                  }
                }}
                placeholder="Ingresa al menos 3 caracteres. Si no encuentras el rubro, puedes crearlo"
                className={`${styles.input} ${!isValid ? styles.inputError : ''}`}
                disabled={disabled || isLoading || isCreating}
              />
            </div>
            
            <button
              type="button"
              className={styles.showAllButton}
              onClick={() => setShowCompleteList(!showCompleteList)}
              disabled={disabled || isLoading || isCreating}
            >
              {showCompleteList ? 'Ocultar' : 'Ver todos'}
            </button>
          </div>

          {!isValid && validationMessage && (
            <div className={styles.validationMessage}>
              <Info size={14} />
              {validationMessage}
            </div>
          )}

          {showCompleteList && (
            <div className={styles.suggestions}>
              {isLoading ? (
                <div className={styles.loadingItem}>Cargando rubros...</div>
              ) : (
                fullCatalogList.map((rubro) => (
                  <div
                    key={rubro.id}
                    className={styles.suggestionItem}
                    onClick={() => handleRubroSelect(rubro)}
                  >
                    {rubro.name}
                    {rubro.users > 0 && (
                      <span className={styles.userCount}>
                        - hay {rubro.users} {rubro.users === 1 ? "profesional" : "profesionales"} en edify
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {showSuggestions && filteredSuggestions.length > 0 && (
            <div className={styles.suggestions}>
              {filteredSuggestions.map((rubro) => (
                <div
                  key={rubro.id}
                  className={styles.suggestionItem}
                  onClick={() => handleRubroSelect(rubro)}
                >
                  {rubro.name}
                  {rubro.users > 0 && (
                    <span className={styles.userCount}>
                      - hay {rubro.users} {rubro.users === 1 ? "profesional" : "profesionales"} en edify
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {showSuggestions && !isValid && (
            <div className={styles.errorItem}>Ingrese solo letras</div>
          )}

          {showSuggestions && filteredSuggestions.length === 0 && isValid && (
            <div className={styles.createNewRubro}>
              <div className={styles.createNewRubroInfo}>
                <Info size={16} />
                <span>No hay rubros que coincidan con tu búsqueda. ¿Quieres crear "{inputValue}"? El equipo de edify te ayudará a encontrar profesionales.</span>
              </div>
              <button
                type="button"
                className={styles.createButton}
                onClick={handleCreateRubro}
                disabled={disabled || isLoading || isCreating}
              >
                {isCreating ? (
                  <>
                    <Loader2 size={16} className={styles.spinner} />
                    Creando...
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Crear "{inputValue}"
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    skillsList: state.register.skillsList || [],
    skillsCatalog: state.register.skills || [],
    newSkill: state.profile.newSkill || null
  };
};

export default connect(mapStateToProps, {
  getAllUsersBySkills,
  getSkills,
  createNewSkill,
  openMainModal,
  closeMainModal
})(RubroSelector);
